import { ExchangeRate } from '../rates/types';
import { rateValidator } from '../../utils/security/rate-validator';
import { rateLimiter, getClientId } from '../../utils/security/rate-limiter';

interface WebSocketMessage {
  type: 'subscribe' | 'unsubscribe' | 'rates' | 'error' | 'heartbeat';
  data?: ExchangeRate[];
  pairs?: string[];
  timestamp?: number;
  source?: string;
}

interface RateSource {
  name: string;
  rates: ExchangeRate[];
  timestamp: Date;
  reliability: number;
}

class RealTimeWebSocketClient {
  private socket: WebSocket | null = null;
  private subscribers = new Set<(rates: ExchangeRate[]) => void>();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private fallbackInterval: NodeJS.Timeout | null = null;
  private isConnecting = false;
  private rateSources: RateSource[] = [];
  private parseErrorCount = 0;
  private maxParseErrors = 5;
  private parseErrorWindow = 30000; // 30 seconds
  private lastParseErrorReset = Date.now();

  async connect(): Promise<void> {
    const clientId = getClientId();
    
    if (!rateLimiter.checkLimit(clientId, 'api')) {
      console.warn('Rate limit exceeded for WebSocket connection');
      this.startFallbackPolling();
      return;
    }

    if (this.isConnecting || (this.socket && this.socket.readyState === WebSocket.OPEN)) {
      return;
    }

    this.isConnecting = true;

    try {
      // Start fallback polling immediately
      this.startFallbackPolling();
      
      // Attempt WebSocket connection to multiple endpoints
      const wsEndpoints = [
        'wss://stream.exchangerate.host/live',
        'wss://echo.websocket.org' // Fallback for demo
      ];

      for (const endpoint of wsEndpoints) {
        try {
          await this.connectToEndpoint(endpoint);
          break;
        } catch (error) {
          console.warn(`Failed to connect to ${endpoint}:`, error);
          continue;
        }
      }
    } catch (error) {
      console.error('All WebSocket endpoints failed:', error);
      this.isConnecting = false;
      this.startFallbackPolling();
    }
  }

  private async connectToEndpoint(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.socket = new WebSocket(url);

      const timeout = setTimeout(() => {
        this.socket?.close();
        reject(new Error('Connection timeout'));
      }, 10000);

      this.socket.onopen = () => {
        clearTimeout(timeout);
        console.log('Real-time WebSocket connected to:', url);
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.startHeartbeat();
        this.subscribeToRates();
        this.stopFallbackPolling();
        resolve();
      };

      this.socket.onmessage = (event) => {
        try {
          // Check if we need to reset parse error count
          const now = Date.now();
          if (now - this.lastParseErrorReset > this.parseErrorWindow) {
            this.parseErrorCount = 0;
            this.lastParseErrorReset = now;
          }

          // Validate that the message is a string
          if (typeof event.data !== 'string') {
            this.handleParseError('Non-JSON message received');
            return;
          }

          // Attempt to parse JSON with specific error handling
          let message: any;
          try {
            message = JSON.parse(event.data);
          } catch (parseError) {
            this.handleParseError(`JSON parse failed: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`);
            return;
          }

          // Validate message structure
          if (!message || typeof message !== 'object' || !message.type) {
            this.handleParseError('Invalid message structure - missing type property');
            return;
          }

          // Type assertion after validation
          const validatedMessage = message as WebSocketMessage;
          this.handleMessage(validatedMessage);
        } catch (error) {
          // This catch block handles any other unexpected errors
          console.error('Unexpected error in WebSocket message handler:', error);
          this.handleParseError('Unexpected message handling error');
        }
      };

      this.socket.onerror = () => {
        clearTimeout(timeout);
        this.isConnecting = false;
        reject(new Error('WebSocket connection error'));
      };

      this.socket.onclose = () => {
        clearTimeout(timeout);
        console.log('WebSocket disconnected');
        this.isConnecting = false;
        this.stopHeartbeat();
        this.handleReconnect();
        this.startFallbackPolling();
      };
    });
  }

  private handleParseError(errorMessage: string): void {
    this.parseErrorCount++;
    console.warn(`WebSocket parse error (${this.parseErrorCount}/${this.maxParseErrors}):`, errorMessage);

    // If we've exceeded the maximum parse errors, disconnect and trigger fallback
    if (this.parseErrorCount >= this.maxParseErrors) {
      console.error('Too many consecutive parse errors. Disconnecting WebSocket and switching to fallback.');
      this.socket?.close();
      this.parseErrorCount = 0;
      this.startFallbackPolling();
    }
  }

  private handleMessage(message: WebSocketMessage): void {
    switch (message.type) {
      case 'rates':
        if (message.data && Array.isArray(message.data)) {
          this.processRateUpdate(message.data, message.source || 'websocket');
        }
        break;
      case 'heartbeat':
        this.sendHeartbeat();
        break;
      case 'error':
        console.error('WebSocket server error:', message.data);
        break;
    }
  }

  private processRateUpdate(rates: ExchangeRate[], sourceName: string): void {
    // Validate rates before processing
    const validatedRates = rates.filter(rate => {
      const validation = rateValidator.validateSingleRate(rate);
      if (!validation.isValid) {
        console.warn('Invalid rate data:', validation.errors);
        return false;
      }
      return true;
    });

    if (validatedRates.length === 0) return;

    // Add to rate sources for cross-validation
    this.rateSources.push({
      name: sourceName,
      rates: validatedRates,
      timestamp: new Date(),
      reliability: 0.9 // WebSocket sources are generally reliable
    });

    // Keep only recent sources (last 5 minutes)
    const fiveMinutesAgo = Date.now() - 300000;
    this.rateSources = this.rateSources.filter(source => 
      source.timestamp.getTime() > fiveMinutesAgo
    );

    // Cross-validate with multiple sources if available
    if (this.rateSources.length > 1) {
      const validation = rateValidator.validateMultipleSources(this.rateSources);
      if (validation.warnings.length > 0) {
        console.warn('Rate validation warnings:', validation.warnings);
      }
      
      // Use best rates from multiple sources
      const bestRates = rateValidator.selectBestRates(this.rateSources);
      this.notifySubscribers(bestRates);
    } else {
      this.notifySubscribers(validatedRates);
    }
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat();
    }, 30000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private sendHeartbeat(): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ 
        type: 'heartbeat', 
        timestamp: Date.now() 
      }));
    }
  }

  private startFallbackPolling(): void {
    if (this.fallbackInterval) return;

    this.fallbackInterval = setInterval(async () => {
      try {
        const { getExchangeRates } = await import('../rates/api');
        const rates = await getExchangeRates();
        this.processRateUpdate(rates, 'fallback-api');
      } catch (error) {
        console.error('Fallback polling failed:', error);
      }
    }, 60000);
  }

  private stopFallbackPolling(): void {
    if (this.fallbackInterval) {
      clearInterval(this.fallbackInterval);
      this.fallbackInterval = null;
    }
  }

  private subscribeToRates(): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      const message: WebSocketMessage = {
        type: 'subscribe',
        pairs: ['USD/NGN', 'EUR/NGN', 'GBP/NGN', 'CAD/NGN', 'AUD/NGN', 'XOF/NGN']
      };
      this.socket.send(JSON.stringify(message));
    }
  }

  private handleReconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
      
      setTimeout(() => {
        console.log(`Reconnecting... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
        this.connect();
      }, delay);
    }
  }

  subscribe(callback: (rates: ExchangeRate[]) => void): () => void {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  private notifySubscribers(rates: ExchangeRate[]): void {
    this.subscribers.forEach(callback => callback(rates));
  }

  disconnect(): void {
    this.stopHeartbeat();
    this.stopFallbackPolling();
    this.parseErrorCount = 0;
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.subscribers.clear();
    this.rateSources = [];
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  getConnectionStatus(): {
    connected: boolean;
    sources: number;
    lastUpdate: Date | null;
  } {
    return {
      connected: this.isConnected(),
      sources: this.rateSources.length,
      lastUpdate: this.rateSources.length > 0 
        ? this.rateSources[this.rateSources.length - 1].timestamp 
        : null
    };
  }
}

export const realTimeClient = new RealTimeWebSocketClient();